import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  RefreshCw,
  Radar,
  Wifi,
  Network,
  Loader2,
  Info,
  Download,
  History,
  FileImage,
  FileText,
  FlaskConical,
  ScanLine,
  Maximize2,
  Minimize2,
  PackageOpen,
  Waypoints,
  Gauge,
  TrendingUp,
} from "lucide-react";
import NetworkScene from "@/components/NetworkScene";
import DeviceCard from "@/components/DeviceCard";
import HistoryPanel from "@/components/HistoryPanel";
import NetworksPanel from "@/components/NetworksPanel";
import RenameModal from "@/components/RenameModal";
import SpeedPanel from "@/components/SpeedPanel";
import LatencyMonitor from "@/components/LatencyMonitor";
import TypeFilter from "@/components/TypeFilter";
import WelcomeOverlay from "@/components/WelcomeOverlay";
import { scanNetwork, getHistory, scanDemo, speedTest, API } from "@/lib/api";
import { loadLabels, persistLabels, deviceKey, customLabel } from "@/lib/labels";
import { loadFlags, persistFlags, isFlagged } from "@/lib/flags";

const AUTO_INTERVAL = 20000;

export default function NetworkView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [selected, setSelected] = useState(null);
  const [lastTime, setLastTime] = useState(null);
  const [newIps, setNewIps] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [portScan, setPortScan] = useState(false);
  const [isFull, setIsFull] = useState(false);
  const [networksOpen, setNetworksOpen] = useState(false);
  const [extraSubnets, setExtraSubnets] = useState(
    () => localStorage.getItem("extraSubnets") || ""
  );
  const [labels, setLabels] = useState(() => loadLabels());
  const [renameTarget, setRenameTarget] = useState(null);
  const [speedOpen, setSpeedOpen] = useState(false);
  const [speedLoading, setSpeedLoading] = useState(false);
  const [speedResult, setSpeedResult] = useState(null);
  const [flags, setFlags] = useState(() => loadFlags());
  const [monitorOpen, setMonitorOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState(new Set());
  const [welcomeOpen, setWelcomeOpen] = useState(
    () => !localStorage.getItem("welcomeSeen")
  );

  const prevIps = useRef(null);
  const canvasEl = useRef(null);
  const rootRef = useRef(null);
  const portScanRef = useRef(false);
  const subnetsRef = useRef(localStorage.getItem("extraSubnets") || "");
  const demoModeRef = useRef(false);
  const timer = useRef(null);
  const clearNewTimer = useRef(null);

  const refreshHistory = useCallback(async () => {
    try {
      setHistory(await getHistory(20));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const runScan = useCallback(
    async (silent = false) => {
      setLoading(true);
      if (!silent) toast.loading("סורק את הרשת...", { id: "scan" });
      try {
        const res = await scanNetwork(portScanRef.current, subnetsRef.current);
        if (demoModeRef.current) return; // demo activated meanwhile; ignore stale scan
        const curIps = new Set(res.devices.map((d) => d.ip));

        // detect newly-joined devices vs previous scan
        if (prevIps.current) {
          const joined = res.devices.filter((d) => !prevIps.current.has(d.ip));
          if (joined.length > 0) {
            setNewIps(new Set(joined.map((d) => d.ip)));
            joined.forEach((d) =>
              toast.warning(`מכשיר חדש התחבר לרשת: ${d.ip}`, {
                description: d.hostname || d.vendor || "מכשיר לא מזוהה",
                duration: 6000,
              })
            );
            clearTimeout(clearNewTimer.current);
            clearNewTimer.current = setTimeout(() => setNewIps(new Set()), 30000);
          }
        }
        prevIps.current = curIps;

        setData(res);
        setLastTime(new Date());
        if (!silent) {
          toast.success(`נמצאו ${res.device_count} מכשירים ברשת`, { id: "scan" });
        }
        refreshHistory();
      } catch (e) {
        console.error(e);
        if (!silent) toast.error("הסריקה נכשלה. נסה שוב.", { id: "scan" });
      } finally {
        setLoading(false);
        setFirstLoad(false);
      }
    },
    [refreshHistory]
  );

  useEffect(() => {
    runScan(false);
    timer.current = setInterval(() => runScan(true), AUTO_INTERVAL);
    return () => {
      clearInterval(timer.current);
      clearTimeout(clearNewTimer.current);
    };
  }, [runScan]);

  const enterDemo = useCallback(async () => {
    setDemoMode(true);
    demoModeRef.current = true;
    clearInterval(timer.current);
    clearTimeout(clearNewTimer.current);
    setNewIps(new Set());
    prevIps.current = null;
    setSelected(null);
    try {
      const res = await scanDemo();
      setData(res);
      setLastTime(new Date());
      toast.success("מצב הדגמה פעיל — רשת לדוגמה עם מכשירים", { id: "scan" });
    } catch (e) {
      console.error(e);
      toast.error("טעינת מצב ההדגמה נכשלה");
    }
  }, []);

  const exitDemo = useCallback(() => {
    setDemoMode(false);
    demoModeRef.current = false;
    prevIps.current = null;
    setSelected(null);
    runScan(false);
    clearInterval(timer.current);
    timer.current = setInterval(() => runScan(true), AUTO_INTERVAL);
  }, [runScan]);

  const toggleDemo = useCallback(() => {
    if (demoMode) exitDemo();
    else enterDemo();
  }, [demoMode, enterDemo, exitDemo]);

  const togglePortScan = useCallback(() => {
    const next = !portScanRef.current;
    portScanRef.current = next;
    setPortScan(next);
    if (!demoMode) {
      toast.message(next ? "סריקת פורטים הופעלה — סורק שירותים..." : "סריקת פורטים כובתה");
      runScan(false);
    }
  }, [demoMode, runScan]);

  const toggleFullscreen = useCallback(() => {
    const el = rootRef.current || document.documentElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  const saveNetworks = useCallback(
    (text) => {
      const clean = (text || "").trim();
      setExtraSubnets(clean);
      subnetsRef.current = clean;
      localStorage.setItem("extraSubnets", clean);
      setNetworksOpen(false);
      if (!demoMode) {
        toast.message("מעדכן רשתות וסורק מחדש...");
        runScan(false);
      }
    },
    [demoMode, runScan]
  );

  const saveLabel = useCallback((device, text) => {
    setLabels((prev) => {
      const next = { ...prev };
      const key = deviceKey(device);
      if (text) next[key] = text;
      else delete next[key];
      persistLabels(next);
      return next;
    });
    setRenameTarget(null);
    toast.success(text ? "השם נשמר" : "השם נמחק");
  }, []);

  const runSpeedTest = useCallback(async () => {
    setSpeedOpen(true);
    setSpeedLoading(true);
    setSpeedResult(null);
    try {
      setSpeedResult(await speedTest());
    } catch (e) {
      console.error(e);
      toast.error("בדיקת המהירות נכשלה");
    } finally {
      setSpeedLoading(false);
    }
  }, []);

  const toggleFlag = useCallback((device) => {
    setFlags((prev) => {
      const next = { ...prev };
      const key = deviceKey(device);
      const now = !next[key];
      if (now) next[key] = true;
      else delete next[key];
      persistFlags(next);
      toast[now ? "warning" : "success"](
        now ? `סומן כחשוד: ${device.ip}` : `הסימון הוסר: ${device.ip}`
      );
      return next;
    });
  }, []);

  const toggleType = useCallback((t) => {
    setTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  }, []);

  const clearTypeFilter = useCallback(() => setTypeFilter(new Set()), []);

  const dismissWelcome = useCallback((remember) => {
    if (remember) localStorage.setItem("welcomeSeen", "1");
    setWelcomeOpen(false);
  }, []);

  const welcomeDemo = useCallback(
    (remember) => {
      dismissWelcome(remember);
      if (!demoModeRef.current) enterDemo();
    },
    [dismissWelcome, enterDemo]
  );

  useEffect(() => {
    const h = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  const exportPNG = useCallback(() => {
    setExportOpen(false);
    if (!canvasEl.current) return;
    try {
      const url = canvasEl.current.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `network-map-${Date.now()}.png`;
      a.click();
      toast.success("מפת הרשת יוצאה כתמונה");
    } catch (e) {
      console.error(e);
      toast.error("ייצוא התמונה נכשל");
    }
  }, []);

  const exportPDF = useCallback(async () => {
    setExportOpen(false);
    if (!canvasEl.current) return;
    try {
      const { default: jsPDF } = await import("jspdf");
      const img = canvasEl.current.toDataURL("image/png");
      const w = canvasEl.current.width;
      const h = canvasEl.current.height;
      const pdf = new jsPDF({
        orientation: w >= h ? "landscape" : "portrait",
        unit: "px",
        format: [w, h + 70],
      });
      pdf.setFontSize(18);
      pdf.text("Network Map / mapat hareshet", 20, 30);
      pdf.setFontSize(11);
      pdf.text(
        `Subnet: ${data?.subnet || "-"}   Devices: ${data?.device_count || 0}   ${new Date().toLocaleString()}`,
        20,
        50
      );
      pdf.addImage(img, "PNG", 0, 65, w, h);
      pdf.save(`network-map-${Date.now()}.pdf`);
      toast.success("מפת הרשת יוצאה כ-PDF");
    } catch (e) {
      console.error(e);
      toast.error("ייצוא ה-PDF נכשל");
    }
  }, [data]);

  const downloadProject = useCallback(() => {
    setExportOpen(false);
    const a = document.createElement("a");
    a.href = `${API}/download/project`;
    a.download = "lan-scanner.zip";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("מוריד את הפרויקט (lan-scanner.zip)", {
      description: "חלץ והרץ start.sh (מק/לינוקס) או start.bat (ווינדוס)",
      duration: 7000,
    });
  }, []);

  const devices = data?.devices || [];
  const router = data?.router || {};
  const presentTypes = Array.from(new Set(devices.map((d) => d.device_type)));
  const visibleDevices =
    typeFilter.size === 0
      ? devices
      : devices.filter((d) => typeFilter.has(d.device_type) || d.ip === router.ip);

  return (
    <div ref={rootRef} dir="rtl" className="relative h-screen w-screen overflow-hidden bg-[#F4F5F7]">
      <Toaster position="top-center" richColors />

      <div
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.5]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.06) 1px, transparent 0)",
          backgroundSize: "26px 26px",
        }}
      />

      <div className="absolute inset-0 z-0">
        {data && (
          <NetworkScene
            router={router}
            devices={visibleDevices}
            selected={selected}
            onSelect={setSelected}
            newIps={newIps}
            onReady={(el) => (canvasEl.current = el)}
            labels={labels}
            flags={flags}
          />
        )}
      </div>

      {/* Top bar */}
      <header className="absolute left-4 right-4 top-4 z-20 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 rounded-2xl border border-white/60 bg-white/70 px-4 py-2.5 shadow-sm backdrop-blur-xl">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0055FF] text-white">
            <Network size={18} strokeWidth={2.4} />
          </span>
          <div>
            <h1 className="text-[15px] font-bold leading-tight text-[#0A0A0A]">
              מפת הרשת התלת-ממדית
              <span className="ms-1.5 align-middle text-[10px] font-semibold text-[#0055FF]">
                · wase3.com
              </span>
            </h1>
            <p className="text-[11px] text-[#52525B]" data-testid="subnet-label">
              {data?.subnets?.length > 1
                ? `${data.subnets.length} רשתות · ${devices.length} מכשירים`
                : data?.subnet
                ? `רשת ${data.subnet}`
                : "מזהה רשת..."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {demoMode ? (
            <div className="hidden items-center gap-2 rounded-2xl border border-amber-300 bg-amber-50/80 px-3 py-2 text-[12px] font-semibold text-amber-700 shadow-sm backdrop-blur-xl lg:flex">
              <FlaskConical size={14} />
              מצב הדגמה
            </div>
          ) : (
            <div className="hidden items-center gap-2 rounded-2xl border border-white/60 bg-white/70 px-3 py-2 text-[12px] text-[#52525B] shadow-sm backdrop-blur-xl lg:flex">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              סריקה אוטומטית פעילה
            </div>
          )}

          <button
            data-testid="demo-toggle"
            onClick={toggleDemo}
            className={`flex items-center gap-2 rounded-2xl border px-3.5 py-2.5 text-[13px] font-semibold shadow-sm backdrop-blur-xl transition-[transform,background-color] duration-200 hover:-translate-y-0.5 ${
              demoMode
                ? "border-amber-300 bg-amber-400 text-white hover:bg-amber-500"
                : "border-white/60 bg-white/70 text-[#0A0A0A] hover:bg-white"
            }`}
          >
            <FlaskConical size={16} />
            <span className="hidden sm:inline">{demoMode ? "צא מהדגמה" : "מצב הדגמה"}</span>
          </button>

          <button
            data-testid="portscan-toggle"
            onClick={togglePortScan}
            title="סריקת פורטים / שירותים"
            className={`flex items-center gap-2 rounded-2xl border px-3.5 py-2.5 text-[13px] font-semibold shadow-sm backdrop-blur-xl transition-[transform,background-color] duration-200 hover:-translate-y-0.5 ${
              portScan
                ? "border-[#0055FF]/40 bg-[#0055FF]/10 text-[#0055FF] hover:bg-[#0055FF]/15"
                : "border-white/60 bg-white/70 text-[#0A0A0A] hover:bg-white"
            }`}
          >
            <ScanLine size={16} />
            <span className="hidden md:inline">פורטים</span>
          </button>

          <button
            data-testid="fullscreen-toggle"
            onClick={toggleFullscreen}
            title="מסך מלא"
            className="flex items-center justify-center rounded-2xl border border-white/60 bg-white/70 p-2.5 text-[#0A0A0A] shadow-sm backdrop-blur-xl transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-white"
          >
            {isFull ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>

          <button
            data-testid="monitor-button"
            onClick={() => setMonitorOpen(true)}
            title="ניטור איחור חי"
            className="flex items-center gap-2 rounded-2xl border border-white/60 bg-white/70 px-3.5 py-2.5 text-[13px] font-semibold text-[#0A0A0A] shadow-sm backdrop-blur-xl transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-white"
          >
            <TrendingUp size={16} />
            <span className="hidden md:inline">ניטור</span>
          </button>

          <button
            data-testid="speed-button"
            onClick={runSpeedTest}
            title="מהירות ואיחור רשת"
            className="flex items-center gap-2 rounded-2xl border border-white/60 bg-white/70 px-3.5 py-2.5 text-[13px] font-semibold text-[#0A0A0A] shadow-sm backdrop-blur-xl transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-white"
          >
            <Gauge size={16} />
            <span className="hidden md:inline">מהירות</span>
          </button>

          <button
            data-testid="networks-button"
            onClick={() => setNetworksOpen(true)}
            title="רשתות לסריקה"
            className="flex items-center gap-2 rounded-2xl border border-white/60 bg-white/70 px-3.5 py-2.5 text-[13px] font-semibold text-[#0A0A0A] shadow-sm backdrop-blur-xl transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-white"
          >
            <Waypoints size={16} />
            <span className="hidden md:inline">רשתות</span>
          </button>

          <button
            data-testid="history-button"
            onClick={() => setHistoryOpen(true)}
            className="flex items-center gap-2 rounded-2xl border border-white/60 bg-white/70 px-3.5 py-2.5 text-[13px] font-semibold text-[#0A0A0A] shadow-sm backdrop-blur-xl transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-white"
          >
            <History size={16} />
            <span className="hidden sm:inline">היסטוריה</span>
          </button>

          {/* Export */}
          <div className="relative">
            <button
              data-testid="export-button"
              onClick={() => setExportOpen((v) => !v)}
              className="flex items-center gap-2 rounded-2xl border border-white/60 bg-white/70 px-3.5 py-2.5 text-[13px] font-semibold text-[#0A0A0A] shadow-sm backdrop-blur-xl transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-white"
            >
              <Download size={16} />
              <span className="hidden sm:inline">ייצוא</span>
            </button>
            <AnimatePresence>
              {exportOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  className="absolute left-0 mt-2 w-56 overflow-hidden rounded-xl border border-black/5 bg-white shadow-lg"
                >
                  <button
                    data-testid="export-png"
                    onClick={exportPNG}
                    className="flex w-full items-center gap-2 px-3 py-2.5 text-right text-[13px] text-[#0A0A0A] transition-colors hover:bg-black/5"
                  >
                    <FileImage size={15} className="text-[#0055FF]" />
                    תמונה (PNG)
                  </button>
                  <button
                    data-testid="export-pdf"
                    onClick={exportPDF}
                    className="flex w-full items-center gap-2 border-t border-black/5 px-3 py-2.5 text-right text-[13px] text-[#0A0A0A] transition-colors hover:bg-black/5"
                  >
                    <FileText size={15} className="text-[#0055FF]" />
                    מסמך (PDF)
                  </button>
                  <button
                    data-testid="download-project"
                    onClick={downloadProject}
                    className="flex w-full items-center gap-2 border-t border-black/5 bg-[#0055FF]/[0.04] px-3 py-2.5 text-right text-[13px] font-semibold text-[#0055FF] transition-colors hover:bg-[#0055FF]/10"
                  >
                    <PackageOpen size={15} />
                    הורד פרויקט (הרצה מקומית)
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            data-testid="scan-button"
            onClick={() => runScan(false)}
            disabled={loading || demoMode}
            className="flex items-center gap-2 rounded-2xl bg-[#0055FF] px-4 py-2.5 text-[13px] font-semibold text-white shadow-md transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[#0047d6] disabled:opacity-60"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <RefreshCw size={16} />
            )}
            <span className="hidden sm:inline">סרוק עכשיו</span>
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <motion.aside
        initial={{ x: 40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="absolute bottom-4 right-4 top-24 z-10 flex w-[330px] max-w-[calc(100vw-2rem)] flex-col rounded-2xl border border-white/60 bg-white/70 shadow-lg backdrop-blur-xl"
        data-testid="device-sidebar"
      >
        <div className="flex items-center justify-between border-b border-black/5 px-4 py-3">
          <div className="flex items-center gap-2">
            <Radar size={17} className="text-[#0055FF]" />
            <h2 className="text-[14px] font-bold text-[#0A0A0A]">מכשירים מחוברים</h2>
          </div>
          <span
            data-testid="device-count"
            className="rounded-full bg-[#0055FF]/10 px-2.5 py-0.5 text-[12px] font-bold text-[#0055FF]"
          >
            {visibleDevices.length}
          </span>
        </div>

        {presentTypes.length > 1 && (
          <div className="border-b border-black/5 px-3 py-2.5">
            <TypeFilter
              types={presentTypes}
              active={typeFilter}
              onToggle={toggleType}
              onAll={clearTypeFilter}
            />
          </div>
        )}

        <div className="flex-1 space-y-2 overflow-y-auto p-3">
          {firstLoad && loading && (
            <div className="flex flex-col items-center gap-3 py-16 text-center text-[#52525B]">
              <Loader2 size={26} className="animate-spin text-[#0055FF]" />
              <p className="text-[13px]">סורק את הרשת שלך...</p>
            </div>
          )}

          {!firstLoad && devices.length === 0 && (
            <div className="flex flex-col items-center gap-3 py-16 text-center text-[#52525B]">
              <Wifi size={26} className="text-[#94A3B8]" />
              <p className="text-[13px]">לא נמצאו מכשירים ברשת.</p>
            </div>
          )}

          <AnimatePresence mode="popLayout">
            {visibleDevices.map((d, i) => (
              <motion.div
                key={d.ip}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ delay: i * 0.03, duration: 0.3 }}
              >
                <DeviceCard
                  device={d}
                  active={selected?.ip === d.ip}
                  onClick={setSelected}
                  isNew={newIps.has(d.ip)}
                  label={customLabel(d, labels)}
                  onRename={setRenameTarget}
                  flagged={isFlagged(d, flags)}
                  onToggleFlag={toggleFlag}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <div className="border-t border-black/5 px-4 py-2.5">
          <p className="flex items-center gap-1.5 text-[11px] text-[#94A3B8]">
            <Info size={12} />
            {lastTime
              ? `עודכן לאחרונה ${lastTime.toLocaleTimeString("he-IL")}`
              : "ממתין לסריקה"}
          </p>
        </div>
      </motion.aside>

      <div className="pointer-events-none absolute bottom-4 left-4 z-10 hidden rounded-xl border border-white/60 bg-white/60 px-3 py-2 text-[11px] text-[#52525B] shadow-sm backdrop-blur-xl md:block">
        גרור לסיבוב · גלול לזום · לחץ על מכשיר לפרטים
      </div>

      <HistoryPanel
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        history={history}
      />

      <NetworksPanel
        open={networksOpen}
        onClose={() => setNetworksOpen(false)}
        value={extraSubnets}
        onSave={saveNetworks}
        scannedSubnets={data?.subnets || []}
      />

      <RenameModal
        device={renameTarget}
        labels={labels}
        onClose={() => setRenameTarget(null)}
        onSave={saveLabel}
      />

      <SpeedPanel
        open={speedOpen}
        onClose={() => setSpeedOpen(false)}
        loading={speedLoading}
        result={speedResult}
      />

      <LatencyMonitor open={monitorOpen} onClose={() => setMonitorOpen(false)} />

      <WelcomeOverlay
        open={welcomeOpen}
        onStart={dismissWelcome}
        onDemo={welcomeDemo}
      />
    </div>
  );
}
